import { Check, Languages, X } from 'lucide-react';
import { type KeyboardEvent, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
  { code: 'kk', label: 'Қазақша' },
  { code: 'ky', label: 'Кыргызча' },
  { code: 'ms', label: 'Bahasa Melayu' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'fil', label: 'Filipino' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'zh', label: '中文' },
  { code: 'ar', label: 'العربية' },
] as const;

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

interface LanguageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LanguageModal({ open, onOpenChange }: LanguageModalProps) {
  const { i18n, t } = useTranslation();
  const current = i18n.language.split('-')[0];
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    triggerRef.current = document.activeElement as HTMLElement;

    const frame = requestAnimationFrame(() => {
      const el = dialogRef.current;
      if (!el) return;
      const focusable = el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      focusable[0]?.focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      triggerRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  function close() {
    onOpenChange(false);
  }

  function handleSelect(code: string) {
    void i18n.changeLanguage(code);
    close();
  }

  function handleDialogKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      close();
      return;
    }

    if (e.key !== 'Tab') return;

    const el = dialogRef.current;
    if (!el) return;

    const focusable = [...el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)];
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  }

  // Portaled to <body> so the fixed overlay covers the full viewport.
  return createPortal(
    <div
      role="presentation"
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]"
      onClick={close}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Modal */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="language-modal-title"
        tabIndex={-1}
        className="relative z-10 w-full max-w-xl overflow-hidden rounded-2xl border bg-popover shadow-2xl"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={handleDialogKeyDown}
      >
        {/* Header row */}
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <Languages className="size-4 shrink-0 text-muted-foreground" />
          <span id="language-modal-title" className="flex-1 text-sm text-muted-foreground">
            {t('languageModal.title', 'Select language')}
          </span>
          <button
            onClick={close}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Language list */}
        <div className="p-2">
          <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            {t('languageModal.available', 'Available languages')}
          </p>
          {LANGUAGES.map(({ code, label }) => (
            <button
              key={code}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-accent hover:text-accent-foreground"
              onClick={() => {
                handleSelect(code);
              }}
            >
              <span className="flex-1 text-start">{label}</span>
              {current === code && <Check className="size-3.5 text-muted-foreground" />}
            </button>
          ))}
        </div>

        {/* Footer hint */}
        <div className="flex items-center gap-4 border-t px-4 py-2.5 text-xs text-muted-foreground">
          <span>
            <kbd className="rounded border px-1 font-mono text-[10px]">↑↓</kbd>{' '}
            {t('languageModal.navigate', 'navigate')}
          </span>
          <span>
            <kbd className="rounded border px-1 font-mono text-[10px]">↵</kbd>{' '}
            {t('languageModal.select', 'select')}
          </span>
          <span>
            <kbd className="rounded border px-1 font-mono text-[10px]">Esc</kbd>{' '}
            {t('languageModal.close', 'close')}
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
